import React, { useState } from "react";
import GenericButton from "@ui-components/Button/GenericButton";
import { useAxiosAuth0 } from "@axios-config/useAxiosAuth0";
import { isTagsManagerEnabled } from "@shared/config/featureFlags";
import { ReactComponent as IconEdit } from "@assets/Icons/IconEdit_KE.svg";

import type { TagsApiMountPoint } from "settings/account/components/TagsManager/requests/request";

import { LegacyTagManagementModal } from "./LegacyTagManagementModal";
import { TagManagementModal } from "./TagManagementModal";
import {
  useResourceTags,
  type ResourceTagSummary,
  type UseResourceTagsReturn,
} from "./useResourceTags";
import { TagsWithPopover } from "kontrolEdge/components/Shared/TagsWithPopover/TagsWithPopover";
import { RichTagsWithPopover } from "kontrolEdge/components/Shared/TagsWithPopover/RichTagsWithPopover";

/**
 * Maximum number of tags rendered inline on the chip strip before
 * collapsing into a `+N` overflow indicator that opens a hover popover
 * with the rest. Keeps the detail card row from growing vertically when
 * the user attaches many tags. The same cap applies to both the legacy
 * (free-text) and picker (rich) renderers.
 */
const CHIP_STRIP_MAX_VISIBLE = 2;

/**
 * Inline tag editor surfaced inside service detail cards.
 *
 * Dual-mode (selected by the `REACT_APP_ENABLE_TAGS_MANAGER` flag):
 *  - **Legacy** (flag off): free-text array stored as a comma-separated
 *    string on the resource record. Driven by `tags` + `onUpdate` props
 *    and the `LegacyTagManagementModal`. Behavior is preserved verbatim.
 *  - **Picker** (flag on): renders the new global Tags Manager
 *    picker (`TagManagementModal`) and reads/writes through the §5
 *    resource-tags endpoints via `useResourceTags`.
 *
 * In picker mode, this component owns the single `useResourceTags`
 * instance shared by the chip strip and the modal so a Save in the modal
 * is reflected immediately in the chip strip without re-fetching the
 * detail GET. Each consumer used to mount its own copy of the hook,
 * which left the strip showing stale tags after Save and made the modal
 * race against an empty initial state.
 *
 * @see `.cursor/skills/tags-manager/SKILL.md` — gating rationale.
 */

interface EditableTagListLegacyProps {
  tags: string[];
  onUpdate: (tags: string[]) => Promise<void>;
  maxTags?: number;
  disabled?: boolean;
  isLoading?: boolean;
}

interface EditableTagListPickerProps {
  resourceType?: string;
  resourceId?: number | string | null;
  mount?: TagsApiMountPoint;
  /**
   * Optional pre-hydrated tags from the resource detail GET inline `tags`
   * field (§5.5). Skips the first §5.1 round-trip on mount. Use the
   * `apiTagsToSummaries` helper from `useResourceTags` to map a raw
   * detail response into this shape. Backbone consumers should leave this
   * undefined — their model has no read controller (§5.5 out-of-scope).
   */
  initialAssignedTags?: ResourceTagSummary[];
  /**
   * Tenant the resource lives in. Forwarded as `?tenantId=` to every Tags
   * API call so Global Admins / Partners viewing a child tenant's resource
   * hit the right scope — same convention `fetchPort` etc. already use on
   * resource detail GETs. Without it the Tags backend resolves the tenant
   * from the JWT, which is wrong when the caller is acting cross-tenant.
   */
  tenantId?: number;
}

export type EditableTagListProps = EditableTagListLegacyProps &
  EditableTagListPickerProps;

/**
 * Free-text chip strip used by the legacy modal flow. Defers to
 * `TagsWithPopover` so we share the same hover/portal mechanic as the
 * rest of the app — only the first `CHIP_STRIP_MAX_VISIBLE` tags
 * render inline, the overflow opens a hover popover with the remaining
 * tags. Keeps detail cards from growing vertically when many tags are
 * attached.
 */
const LegacyChips: React.FC<{ tags: string[] }> = ({ tags }) =>
  tags.length === 0 ? null : (
    <div className="flex justify-end min-w-0">
      <TagsWithPopover
        tags={tags}
        maxVisible={CHIP_STRIP_MAX_VISIBLE}
        triggerSingleRow
        constrainToContainer
      />
    </div>
  );

/**
 * Presentational chip strip — pure projection of `assignedTags`. The
 * controller hook lives one level up so the modal and the strip share a
 * single `assignedTags` state and stay in sync after Save.
 *
 * Uses {@link RichTagsWithPopover} so the same `+N` overflow + hover
 * popover behavior applies to the new rich (color/category) chips,
 * matching the legacy strip's truncation rules.
 */
const PickerChips: React.FC<{
  assignedTags: ResourceTagSummary[];
  loading: boolean;
}> = ({ assignedTags, loading }) => {
  if (loading && assignedTags.length === 0) {
    return (
      <div className="flex flex-wrap items-center gap-2 justify-end">
        <span className="inline-flex items-center gap-1 rounded-md px-2 py-1 h-6 text-xs bg-semantic-bg-secondary text-semantic-text-secondary">
          Loading…
        </span>
      </div>
    );
  }

  if (assignedTags.length === 0) return null;

  return (
    <div className="flex justify-end min-w-0">
      <RichTagsWithPopover
        tags={assignedTags}
        maxVisible={CHIP_STRIP_MAX_VISIBLE}
        triggerSingleRow
        constrainToContainer
      />
    </div>
  );
};

/**
 * Decides whether the new picker can run for this caller.
 *
 * - Picker contract present (`resourceType` + numeric-coercible `resourceId`).
 * - Tags Manager flag enabled (`REACT_APP_ENABLE_TAGS_MANAGER`). When off, the
 *   picker is disabled and the legacy free-text modal is used, matching the
 *   backend `TAGS_MANAGER_ENABLED` gate so both can coexist.
 *
 * Non-numeric ids (e.g. seed values like `"port-001"`) fall back to the
 * legacy modal: the §5 endpoints expect an integer `resourceId`, so the
 * picker would otherwise mount but never be able to save.
 */
function shouldUsePicker(
  resourceType: string | undefined,
  resourceId: number | string | null | undefined
): resourceType is string {
  if (!isTagsManagerEnabled()) return false;
  if (!resourceType) return false;
  if (resourceId === null || resourceId === undefined) return false;
  if (typeof resourceId === "number") {
    return Number.isFinite(resourceId) && Number.isInteger(resourceId);
  }
  const trimmed = resourceId.trim();
  if (trimmed === "") return false;
  const n = Number(trimmed);
  return Number.isFinite(n) && Number.isInteger(n);
}

/**
 * Owns the single `useResourceTags` instance for one resource. Renders the
 * chip strip + the modal, sharing `assignedTags` and the mutation methods
 * between them so a Save reflects in the strip immediately.
 */
const PickerEditableTagList: React.FC<{
  resourceType: string;
  resourceId: number | string;
  mount: TagsApiMountPoint;
  maxTags?: number;
  disabled: boolean;
  isLoading: boolean;
  initialAssignedTags?: ResourceTagSummary[];
  tenantId?: number;
}> = ({
  resourceType,
  resourceId,
  mount,
  maxTags,
  disabled,
  isLoading,
  initialAssignedTags,
  tenantId,
}) => {
  const api = useAxiosAuth0();
  const controller: UseResourceTagsReturn = useResourceTags({
    api,
    mount,
    resourceType,
    resourceId,
    initialAssignedTags,
    tenantId,
  });
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenModal = () => {
    if (disabled || isLoading) return;
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      <div className="flex flex-col gap-2 min-w-0">
        <div className="flex items-center justify-end gap-2">
          <GenericButton
            label="Edit"
            variant="secondary"
            shape="ghost"
            size="xsm"
            onClick={handleOpenModal}
            disabled={disabled || isLoading}
            customHeight="24px"
            className="flex-shrink-0"
            icon={<IconEdit className="w-4 h-4" />}
            iconPosition="left"
          />
        </div>

        <PickerChips
          assignedTags={controller.assignedTags}
          loading={controller.loading}
        />
      </div>

      <TagManagementModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        controller={controller}
        maxTags={maxTags}
      />
    </>
  );
};

/**
 * Legacy variant — keeps the free-text array editor untouched while
 * production runs without the new Tags Manager backend.
 */
const LegacyEditableTagList: React.FC<{
  tags: string[];
  onUpdate: (tags: string[]) => Promise<void>;
  maxTags?: number;
  disabled: boolean;
  isLoading: boolean;
}> = ({ tags, onUpdate, maxTags, disabled, isLoading }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenModal = () => {
    if (disabled || isLoading) return;
    setIsModalOpen(true);
  };

  const buttonLabel = tags.length === 0 ? "+ Add" : "Edit";
  const showEditIcon = tags.length > 0;

  return (
    <>
      <div className="flex flex-col gap-2 min-w-0">
        <div className="flex items-center justify-end gap-2">
          <GenericButton
            label={buttonLabel}
            variant="secondary"
            shape="ghost"
            size="xsm"
            onClick={handleOpenModal}
            disabled={disabled || isLoading}
            customHeight="24px"
            className="flex-shrink-0"
            icon={showEditIcon ? <IconEdit className="w-4 h-4" /> : undefined}
            iconPosition="left"
          />
        </div>
        <LegacyChips tags={tags} />
      </div>

      <LegacyTagManagementModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        initialTags={tags}
        onSave={onUpdate}
        maxTags={maxTags}
      />
    </>
  );
};

export const EditableTagList: React.FC<EditableTagListProps> = ({
  tags,
  onUpdate,
  maxTags,
  disabled = false,
  isLoading = false,
  resourceType,
  resourceId,
  mount = "kontrol-edge",
  initialAssignedTags,
  tenantId,
}) => {
  if (shouldUsePicker(resourceType, resourceId)) {
    return (
      <PickerEditableTagList
        resourceType={resourceType as string}
        resourceId={resourceId as number | string}
        mount={mount}
        maxTags={maxTags}
        disabled={disabled}
        isLoading={isLoading}
        initialAssignedTags={initialAssignedTags}
        tenantId={tenantId}
      />
    );
  }
  return (
    <LegacyEditableTagList
      tags={tags}
      onUpdate={onUpdate}
      maxTags={maxTags}
      disabled={disabled}
      isLoading={isLoading}
    />
  );
};
